import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for modal accessibility
 * - Closes modal on Escape key
 * - Traps focus within modal
 * - Returns focus to trigger element on close
 */
const useModalAccessibility = (onClose, isOpen = true) => {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Store the previously focused element when modal opens
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement;
    }
  }, [isOpen]);

  // Handle Escape key
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  // Handle focus trapping
  const handleFocusTrap = useCallback((event) => {
    if (event.key !== 'Tab' || !modalRef.current) return;

    const focusableElements = modalRef.current.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        lastElement.focus();
        event.preventDefault();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        firstElement.focus();
        event.preventDefault();
      }
    }
  }, []);

  // Set up event listeners and focus management
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyEvents = (event) => {
      handleKeyDown(event);
      handleFocusTrap(event);
    };

    document.addEventListener('keydown', handleKeyEvents);

    // Focus the first focusable element in the modal
    const focusableElements = modalRef.current?.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements?.length > 0) {
      focusableElements[0].focus();
    }

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyEvents);
      document.body.style.overflow = '';

      // Return focus to the previously focused element
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    };
  }, [isOpen, handleKeyDown, handleFocusTrap]);

  return modalRef;
};

export default useModalAccessibility;
